import React, { useState } from 'react';
import { ChevronDown, ChevronRight, LucideIcon } from 'lucide-react';

interface CollapsibleSectionProps {
  title: string;
  icon: LucideIcon;
  badge?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  darkMode?: boolean;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon: Icon,
  badge,
  defaultOpen = false,
  children,
  darkMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`border rounded-lg ${darkMode ? 'border-gray-700' : 'border-gray-200'} ${!isOpen ? 'overflow-hidden' : ''}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full py-2.5 px-3 flex items-center justify-between transition-colors ${
          isOpen
            ? (darkMode ? 'bg-gray-900' : 'bg-white')
            : (darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-50 hover:bg-gray-100')
        }`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{title}</span>
          {badge && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
            }`}>
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className={`h-4 w-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        ) : (
          <ChevronRight className={`h-4 w-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        )}
      </button>

      <div
        className={`transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className={`p-3 pt-0 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
